import { clamp } from '../util/math';
import { indexAtOrBefore } from '../util/search';
import type { BeatGrid } from './types';

// Bars are numbered from 1 starting at the first downbeat; beats before it form bar 0 (a pickup).

export function beatIndexAt(grid: BeatGrid, t: number): number {
  return indexAtOrBefore(grid.beats, t);
}

/** Bar number of a beat (0 for pickup beats before the first downbeat). */
export function barOfBeat(grid: BeatGrid, beatIndex: number): number {
  if (beatIndex < grid.firstDownbeat) return 0;
  return Math.floor((beatIndex - grid.firstDownbeat) / grid.beatsPerBar) + 1;
}

/** Position of a beat inside its bar, 1…beatsPerBar. */
export function beatInBar(grid: BeatGrid, beatIndex: number): number {
  const rel = beatIndex - grid.firstDownbeat;
  return (((rel % grid.beatsPerBar) + grid.beatsPerBar) % grid.beatsPerBar) + 1;
}

export function barCount(grid: BeatGrid): number {
  return grid.beats.length === 0 ? 0 : barOfBeat(grid, grid.beats.length - 1);
}

/** End time of the last beat: one beat period after it, clipped to the song. */
export function gridEnd(grid: BeatGrid, duration: number): number {
  const last = grid.beats.at(-1);
  return last === undefined ? duration : Math.min(duration, last + 60 / grid.bpm);
}

/** Start time of a beat; indices past the end map to the end of the last beat. */
export function beatTime(grid: BeatGrid, index: number, duration: number): number {
  if (index <= 0) return grid.beats[0] ?? 0;
  return grid.beats[index] ?? gridEnd(grid, duration);
}

/** Time range covering bars `from`…`to` inclusive (1-based, clamped to the song's bars). */
export function barRange(
  grid: BeatGrid,
  from: number,
  to: number,
  duration: number,
): { start: number; end: number } {
  const bars = Math.max(1, barCount(grid));
  const a = clamp(Math.min(from, to), 1, bars);
  const b = clamp(Math.max(from, to), 1, bars);
  const startBeat = grid.firstDownbeat + (a - 1) * grid.beatsPerBar;
  const endBeat = grid.firstDownbeat + b * grid.beatsPerBar;
  return { start: beatTime(grid, startBeat, duration), end: beatTime(grid, endBeat, duration) };
}

/** Bars `from`…`to` covered by a time range (inverse of barRange, for labels). */
export function barsOfRange(
  grid: BeatGrid,
  range: { start: number; end: number },
): [number, number] {
  const first = Math.max(1, barOfBeat(grid, Math.max(0, beatIndexAt(grid, range.start + 1e-3))));
  const last = Math.max(first, barOfBeat(grid, Math.max(0, beatIndexAt(grid, range.end - 1e-3))));
  return [first, last];
}

/**
 * Continuous beat coordinate of time `t`: 3.5 means halfway through beat 3. Before the first
 * beat and after the last it extrapolates with the average beat period. Used by the timeline,
 * which spaces blocks by beats so tempo drift doesn't distort it.
 */
export function beatPosition(grid: BeatGrid, t: number): number {
  const { beats } = grid;
  const period = 60 / grid.bpm;
  if (beats.length === 0) return t / period;
  const i = beatIndexAt(grid, t);
  if (i < 0) return (t - beats[0]!) / period;
  const start = beats[i]!;
  const next = beats[i + 1];
  const length = next !== undefined ? next - start : period;
  return i + (t - start) / length;
}

/** Nearest beat start to `t` (F3.1: seeking from the chord grid snaps to beats). */
export function snapToBeat(grid: BeatGrid, t: number): number {
  const i = beatIndexAt(grid, t);
  const before = grid.beats[i];
  const after = grid.beats[i + 1];
  if (before === undefined) return after ?? t;
  if (after === undefined) return before;
  return t - before <= after - t ? before : after;
}
